import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DIVISIONS } from "@/data/divisions";
import Icon from "@/components/Icon";
import { useLead } from "@/context/LeadContext";

export default function DivisionPage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { openLead } = useLead();
  const d = DIVISIONS[key];

  useEffect(() => {
    if (!d) {
      navigate("/", { replace: true });
      return;
    }
    document.title = `${d.name} | Proactive UMS Corporate`;
    window.scrollTo(0, 0);
  }, [key, d, navigate]);

  if (!d) return null;
  const goEco = () => navigate("/", { state: { scrollTo: "ecosystem" } });

  return (
    <div className="dp" data-testid={`division-page-${d.key}`}>
      <section className="dp-hero">
        <div className="dp-hero__bg" style={{ backgroundImage: `url(${d.image})` }} />
        <div className="dp-hero__veil" />
        <div className="container">
        <div className="dp-hero__inner">
          <button className="dp-breadcrumb" onClick={goEco} data-testid="dp-breadcrumb">
            <Icon name="ArrowLeft" size={14} /> The PUMS Ecosystem
          </button>
          <div className="dp-hero__logo dp-hero__logo--light">
            <img src={`/assets/logo-eco-${d.key}.png`} alt={d.name} />
          </div>
          <span className="eyebrow">{d.eyebrow}</span>
          <h1 className="dp-hero__title display">{d.heroHeading}</h1>
          <p className="dp-hero__tagline">{d.tagline}</p>
          <p className="dp-hero__blurb">{d.heroBlurb}</p>
          <div className="dp-hero__actions">
            <button className="btn btn--green" onClick={() => openLead("demo")} data-testid="dp-request-demo">
              Request a Demo <Icon name="ArrowRight" size={16} />
            </button>
            <button className="btn btn--outline-dark" onClick={goEco} data-testid="dp-back">
              Back to Ecosystem
            </button>
          </div>
        </div>
        </div>
      </section>

      <section className="dp-overview">
        <div className="container dp-overview__grid">
          <div className="dp-overview__copy">
            <h2 className="dp-h2">Overview</h2>
            {d.overview.map((p, i) => <p key={i} className="dp-p">{p}</p>)}
          </div>
          <aside className="dp-capabilities">
            <h3 className="dp-cap__title">Key Capabilities</h3>
            <ul>
              {d.capabilities.map((c, i) => (
                <li key={i}><Icon name="CheckCircle2" size={18} /> <span>{c}</span></li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      <section className="dp-services">
        <div className="container">
          <div className="sec-head">
            <div className="sec-head__left">
              <h2 className="sec-head__title">Services<span className="sec-head__rule" /></h2>
              <p className="sec-head__sub">{d.servicesSub}</p>
            </div>
          </div>
          <div className="dp-services__grid">
            {d.services.map((s) => (
              <div className="dp-service" key={s.title} data-testid={`dp-service-${s.title}`}>
                <span className="dp-service__ic"><Icon name={s.icon} size={24} /></span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="dp-stats">
        <div className="container dp-stats__row">
          {d.stats.map((s) => (
            <div className="dp-stat" key={s.label}>
              <strong>{s.value}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="dp-cta">
        <img src="/assets/cta-family.png" className="dp-cta__bg" alt="" />
        <div className="container dp-cta__inner">
          <div className="dp-cta__copy">
            <h2 className="dp-cta__title display">Ready to move care forward?</h2>
            <p>Partner with {d.name} and the Proactive UMS ecosystem.</p>
          </div>
          <div className="dp-cta__actions">
            <button className="btn btn--green" onClick={() => openLead("demo")} data-testid="dp-cta-demo">
              Request a Demo <Icon name="ArrowRight" size={16} />
            </button>
            <button className="btn btn--outline-dark" onClick={() => openLead("contact")} data-testid="dp-cta-contact">
              <Icon name="Mail" size={16} /> Contact Our Team
            </button>
          </div>
        </div>
      </section>

      <section className="dp-siblings">
        <div className="container">
          <h3 className="dp-siblings__title">Explore other divisions</h3>
          <div className="dp-siblings__row">
            {Object.values(DIVISIONS).filter((x) => x.key !== key).map((x) => (
              <button key={x.key} className="dp-sibling" onClick={() => navigate(`/division/${x.key}`)} data-testid={`dp-sibling-${x.key}`}>
                <span className="dp-sibling__logo"><img src={`/assets/logo-eco-${x.key}.png`} alt={x.name} loading="lazy" /></span>
                <span className="dp-sibling__go"><Icon name="ArrowRight" size={14} /></span>
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
